/**
 * Minimal PKCE helpers for OIDC authorization code flow.
 *
 * Supports both secure contexts (Web Crypto) and non-secure origins
 * such as bare IP addresses served over HTTP, where `crypto.subtle` is
 * unavailable. In the latter case we fall back to a pure-JS SHA-256.
 */

import { sha256 } from "js-sha256";

function base64UrlEncode(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function generateRandomString(length = 64): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export interface PKCEPair {
  codeVerifier: string;
  codeChallenge: string;
}

export async function generatePKCE(): Promise<PKCEPair> {
  const codeVerifier = generateRandomString(64);
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);

  let digest: ArrayBuffer;
  if (crypto.subtle) {
    digest = await crypto.subtle.digest("SHA-256", data);
  } else {
    // Non-secure origins (e.g. bare IP over HTTP) do not expose crypto.subtle.
    digest = sha256.arrayBuffer(codeVerifier);
  }
  const codeChallenge = base64UrlEncode(digest);
  return { codeVerifier, codeChallenge };
}

export function generateState(): string {
  return generateRandomString(32);
}

export interface OIDCConfig {
  enabled: boolean;
  authorize_url: string;
  client_id: string;
  redirect_uri: string;
  scope: string;
}
